import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  acceptChallengeUseCase,
  addRegisteredParticipantUseCase,
  createTournamentUseCase,
  declineChallengeUseCase,
  getChallengesReceivedUseCase,
  getChallengesSentUseCase,
} from '@/src/core/dependencies';
import { ChallengeWithUser, ModerationError } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { ChallengesScreen } from '@/src/presentation/screens/ChallengesScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function ChallengesContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [receivedChallenges, setReceivedChallenges] = useState<ChallengeWithUser[]>([]);
  const [sentChallenges, setSentChallenges] = useState<ChallengeWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreReceived, setHasMoreReceived] = useState(true);
  const [hasMoreSent, setHasMoreSent] = useState(true);
  const [pageReceived, setPageReceived] = useState(0);
  const [pageSent, setPageSent] = useState(0);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');

  useFocusEffect(
    useCallback(() => {
      loadChallenges();
    }, [])
  );

  const loadChallenges = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setPageReceived(0);
      setPageSent(0);
      setHasMoreReceived(true);
      setHasMoreSent(true);

      const [received, sent] = await Promise.all([
        getChallengesReceivedUseCase.execute(user.id, PAGE_SIZE, 0),
        getChallengesSentUseCase.execute(user.id, PAGE_SIZE, 0),
      ]);

      setReceivedChallenges(received);
      setSentChallenges(sent);
      setHasMoreReceived(received.length === PAGE_SIZE);
      setHasMoreSent(sent.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Challenges', 'Failed to load challenges', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReceived = async () => {
    if (loadingMore || !hasMoreReceived || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = pageReceived + 1;
      const data = await getChallengesReceivedUseCase.execute(
        user.id,
        PAGE_SIZE,
        nextPage * PAGE_SIZE
      );

      if (data.length > 0) {
        setReceivedChallenges((prev) => [...prev, ...data]);
        setPageReceived(nextPage);
        setHasMoreReceived(data.length === PAGE_SIZE);
      } else {
        setHasMoreReceived(false);
      }
    } catch (error) {
      logger.error('Challenges', 'Failed to load more received challenges', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreSent = async () => {
    if (loadingMore || !hasMoreSent || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = pageSent + 1;
      const data = await getChallengesSentUseCase.execute(user.id, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setSentChallenges((prev) => [...prev, ...data]);
        setPageSent(nextPage);
        setHasMoreSent(data.length === PAGE_SIZE);
      } else {
        setHasMoreSent(false);
      }
    } catch (error) {
      logger.error('Challenges', 'Failed to load more sent challenges', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAccept = async (challengeId: string, challengerName?: string) => {
    if (!user) return;

    try {
      // Create a 1v1 tournament for the challenge
      const challenge = receivedChallenges.find((c) => c.id === challengeId);
      if (!challenge) return;

      // Get challenger display name (name or email prefix as fallback)
      const challengerDisplayName = challengerName || challenge.userEmail?.split('@')[0] || 'Challenger';
      // Get current user display name
      const myDisplayName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'You';

      const tournament = await createTournamentUseCase.execute({
        name: `Challenge: ${challengerDisplayName} vs ${myDisplayName}`,
        description: challenge.message || '1v1 Challenge Match',
        tournamentType: 'single_elimination',
        maxParticipants: 2,
        isPublic: false,
        userId: user.id,
      });

      // Add both participants to the tournament
      await Promise.all([
        addRegisteredParticipantUseCase.execute({
          tournamentId: tournament.id,
          userId: challenge.challengerId,
          userName: challengerDisplayName,
        }),
        addRegisteredParticipantUseCase.execute({
          tournamentId: tournament.id,
          userId: user.id,
          userName: myDisplayName,
        }),
      ]);

      // Update challenge with tournament ID
      await acceptChallengeUseCase.execute(challengeId, tournament.id);

      // Remove from received challenges
      setReceivedChallenges((prev) => prev.filter((c) => c.id !== challengeId));

      platformAlert(t('common.success'), t('alerts.challengeAcceptedTournament'));
      router.push(`/tournament/${tournament.id}`);
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('Challenges', 'Failed to accept challenge', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
      }
    }
  };

  const handleDecline = async (challengeId: string) => {
    try {
      await declineChallengeUseCase.execute(challengeId);
      setReceivedChallenges((prev) => prev.filter((c) => c.id !== challengeId));
      platformAlert(t('common.success'), t('alerts.challengeDeclined'));
    } catch (error) {
      logger.error('Challenges', 'Failed to decline challenge', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  return (
    <ChallengesScreen
      receivedChallenges={receivedChallenges}
      sentChallenges={sentChallenges}
      loading={loading}
      loadingMore={loadingMore}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onUserPress={handleUserPress}
      onTournamentPress={handleTournamentPress}
      onRefresh={loadChallenges}
      onLoadMoreReceived={loadMoreReceived}
      onLoadMoreSent={loadMoreSent}
      colors={colors}
    />
  );
}
